import { Translation, TranslationsWithId, CURRENCY } from 'src/types';
import { Visitor } from 'src/features/visitor/types';
import { AdministrativeTranslation } from 'src/features/zone/utils/types';
import { ENTITY_TYPE } from 'src/features/entity/types';

export enum PERIOD_TYPE {
    STAY = 'stay',
    TRANSIT = 'transit',
    EXIT = 'exit',
}

export enum VISITOR_CATEGORY {
    CLIENT = 'client',
    VIP = 'vip',
    RESIDENT = 'resident',
    STAFF = 'staff',
    SERVICE = 'service',
    SUBSCRIPTION = 'subscription',
    RESERVATION = 'reservation',
    MEMBERSHIP = 'membership',
}

export interface ActiveStay {
    id: string;
    visitor: Visitor;
    visitor_owner: {
        name: string;
        id: string;
        type: ENTITY_TYPE;
    };
    period_visitor_category: VISITOR_CATEGORY;
    created_at: string;
    modified_at: string;
    ref: string;
    entry_time: string;
    period_entry_point: TranslationsWithId;
    period_zone: TranslationsWithId;
    period_exit_point: TranslationsWithId;
    entry_gateway: TranslationsWithId;
    main_identifier: {
        value: string;
    };
    entry_photo: string | null;
    additional_identifiers: { value: string }[];
    duration: string;
    exit_time?: string;
    total_amounts?: {
        total_due_amount_with_tax: number;
        total_remaining_amount: number;
        total_due_amount: number;
        total_tax_amount: number;
        total_paid_amount: number;
    };
    currency: CURRENCY;
}

export type ZoneStayPeriod = {
    id: string;
    zone: TranslationsWithId;
    entry_pass: {
        gateway: {
            id: string;
            translations?: Translation[];
        };
        id: string;
        time: string;
    };
    exit_pass: {
        gateway: {
            id: string;
            translations?: Translation[];
        };
        id: string;
        time: string;
    };
    created_at: string;
    modified_at: string;
    start_date: string;
    end_date: string;
};

export type ActiveStayPeriod = {
    id: string;
    created_at: string;
    modified_at: string;
    number: number;
    start: string;
    end?: string;
    duration?: string;
    type: PERIOD_TYPE;
    due_amount?: number;
    tax_amount?: number;
    due_amount_with_tax?: number;
    currency: CURRENCY;
    stay: {
        id: string;
        period_visitor_category: string;
    };
    zones: TranslationsWithId[];
    space: string;
    entry_pass: {
        gateway: TranslationsWithId;
        id: string;
        time: string;
    };
    exit_pass: {
        gateway: TranslationsWithId;
        id: string;
        time: string;
    };
    membership?: {
        id: string;
        ref: string;
        membership_type_id: string;
    };
    due_amount_calculated_until?: string;
    pricing_plan: TranslationsWithId & {
        currency: CURRENCY;
    };
    zone_stay_periods: ZoneStayPeriod[];
    validated_amount: number;
    validated_time: string;
    visitor_category: VISITOR_CATEGORY;
};

export interface PastStay extends Omit<ActiveStay, 'period_entry_point' | 'period_exit_point'> {
    exit_time: string;
    exit_photo: string | null;
    exit_gateway: TranslationsWithId;
    entry_gateway: TranslationsWithId;
}

export enum STAY_TYPE {
    ACTIVE = 'active',
    PAST = 'past',
}

enum BILLING_TYPE {
    POST = 'post',
    PRE = 'pre',
}

enum CALCULATION_TYPE {
    PER_FIXED_TICK = 'per_fixed_tick',
    PER_PERIODS_COMPOUND = 'per_periods_compound',
    PER_PERIODS_TOTAL = 'per_periods_total',
    FIXED_PRICE = 'fixed_price',
}

enum FREE_TIME_TYPE {
    EVERY_VISIT = 'every_visit',
    CAPPED = 'capped',
}

enum CAPPING {
    NONE = 'none',
    ONE_TIME = 'one_time',
    REPEATING = 'repeating',
}

enum PRICING_PLAN_STATUS {
    DRAFT = 'draft',
    PUBLISHED = 'published',
}

enum TICK_TYPE {
    MINUTE = 'minute',
    HOUR = 'hour',
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month',
}

type PricingVersion = {
    id: string;
    version_number: number;
    valid_from: string;
    valid_to: string | null;
};

export type PricingPlan = {
    id: string;
    translations: AdministrativeTranslation[];
    created_at: string;
    modified_at: string;
    billing_type: BILLING_TYPE;
    status: PRICING_PLAN_STATUS;
    calculation_type: CALCULATION_TYPE;
    version_number: number;
    valid_from: string;
    valid_to: string;
    tax_percent: string;
    currency: CURRENCY;
    free_time_type: FREE_TIME_TYPE;
    free_time_cap_minutes: number | null;
    free_time_minutes: number | null;
    exit_time: number | null;
    exit_time_grace: number | null;
    capping: CAPPING;
    is_current_version: boolean;
    identity: string;
    previous_version: PricingVersion | null;
    next_version: PricingVersion | null;
    tick: {
        tick_type: TICK_TYPE;
        tick_minutes: number;
    };
    fixed_rate?: {
        rate_with_tax: number;
    };
};
